import { watch } from 'vue';
import { Dark } from 'quasar';
import { useLocalStorage } from '@vueuse/core';

/**
 * App theme preference. Persisted per-device in localStorage (key: `theme`),
 * mirroring how the locale is stored. `auto` follows the OS `prefers-color-scheme`
 * — Quasar's Dark plugin tracks system changes natively, so no extra listener is needed.
 */
export type ThemeMode = 'light' | 'dark' | 'auto';

const STORAGE_KEY = 'theme';

// Single shared ref across all callers.
const themeMode = useLocalStorage<ThemeMode>(STORAGE_KEY, 'auto');

// Map our friendly mode onto the values Quasar's Dark.set() accepts.
function toDarkValue(mode: ThemeMode): boolean | 'auto' {
  if (mode === 'light') return false;
  if (mode === 'dark') return true;
  return 'auto';
}

/** Apply the currently stored theme to Quasar. Safe to call at boot and on change. */
function applyTheme() {
  Dark.set(toDarkValue(themeMode.value));
}

/** Change the theme; persists to localStorage and applies immediately (no reload). */
function setTheme(mode: ThemeMode) {
  themeMode.value = mode;
}

// Keep Quasar in sync whenever the stored preference changes (e.g. from the selector).
watch(themeMode, applyTheme);

export function useTheme() {
  return { themeMode, setTheme, applyTheme };
}
